import { useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { read, utils } from "xlsx";
import { SectionTitle, CmpTable, Loader } from "../shared";
import SubComponent from "./SubComponent";
import { CREATE_VEEVA_DATA, GET_ALL_VEEVA_DATA } from "./query";
import COUNTRY_LIST from "../../utils/countryList";
import useGlobalMutation from "../../hooks/useGlobalMutation";
import { clearCache } from "../../utils";

const VeevaList = () => {
    const fileInputRef = useRef(null);

    const { submitMutation, loading } = useGlobalMutation(CREATE_VEEVA_DATA, (cache) => {
        clearCache(cache, "allVeevaData");
    });

    const veevaDataImport = (e) => {
        try {
            const file = e.target.files[0];
            const reader = new FileReader();

            reader.onload = async (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = read(data, { type: "array" });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const excelDataData = utils.sheet_to_json(worksheet);
                if (excelDataData.length > 0) {
                    await insertIntoDB(excelDataData);
                } else {
                    toast.error("No data found!!!");
                }
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            };
            reader.readAsArrayBuffer(file);
        } catch (error) {
            toast.error(error.message);
        }
    };

    const insertIntoDB = async (data) => {
        const veevaId = [];
        const title = [];
        const country = [];
        data.forEach((item) => {
            if (item.veevaId && item.title && item.country) {
                veevaId.push(item.veevaId);
                title.push(item.title);
                country.push(item.country.toLowerCase());
            } else {
                toast.error("Something went wrong! Check the file and try again.");
            }
        });

        const variables = {
            veevaId,
            title,
            country,
        };

        const dataPath = "veevaDataInsert.veevaData";

        await submitMutation(variables, dataPath, `Veeva Data Inserted Successfully`);
    };

    const columns = [
        {
            header: "Veeva ID",
            accessorKey: "veevaId",
        },
        {
            header: "Title",
            accessorKey: "title",
        },
        {
            header: "Country",
            accessorKey: "country",
            cell: (cell) => COUNTRY_LIST[cell.getValue().toUpperCase()],
        },
    ];

    const renderSubComponent = (data) => {
        return <SubComponent data={data.row.original} />;
    };

    const { country, userType } = useSelector((state) => state.users.currentUser);
    const variables =
        userType === 1
            ? {
                  filter: {
                      country: {
                          inInsensitive: country,
                      },
                  },
              }
            : {};

    return (
        <>
            {loading && <Loader />}
            <SectionTitle
                title="Veeva Data"
                buttonUrl={`/abbvie-pro/veeva-data/create`}
                backUrl="/abbvie-pro"
            >
                <div className="align-self-center mr-3">
                    <input
                        onChange={veevaDataImport}
                        type="file"
                        className="custom-file-input sr-only"
                        id="inputGroupFile"
                        ref={fileInputRef}
                    />

                    <label
                        style={{ cursor: "pointer" }}
                        className="btn btn-outline-primary mb-0 d-flex align-items-center cursor-pointer"
                        htmlFor="inputGroupFile"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-2"
                            height="1em"
                            viewBox="0 0 384 512"
                            fill="currentColor"
                        >
                            <path d="M48 448V64c0-8.8 7.2-16 16-16H224v80c0 17.7 14.3 32 32 32h80V448c0 8.8-7.2 16-16 16H64c-8.8 0-16-7.2-16-16zM64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H320c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L274.7 18.7C262.7 6.7 246.5 0 229.5 0H64zm90.9 233.3c-8.1-10.5-23.2-12.3-33.7-4.2s-12.3 23.2-4.2 33.7L161.6 320l-44.5 57.3c-8.1 10.5-6.3 25.5 4.2 33.7s25.5 6.3 33.7-4.2L192 359.1l37.1 47.6c8.1 10.5 23.2 12.3 33.7 4.2s12.3-23.2 4.2-33.7L222.4 320l44.5-57.3c8.1-10.5 6.3-25.5-4.2-33.7s-25.5-6.3-33.7 4.2L192 280.9l-37.1-47.6z" />
                        </svg>
                        Import Excel
                    </label>
                </div>
            </SectionTitle>

            <CmpTable
                QUERY={GET_ALL_VEEVA_DATA}
                getRowCanExpand={() => true}
                headers={columns}
                renderSubComponent={renderSubComponent}
                dataPath="allVeevaData"
                variables={variables}
                tableType="veevaTable"
                cacheName="allVeevaData"
            />
        </>
    );
};

export default VeevaList;
